import React, { useState, useEffect } from "react";

import { THEME_MAPPINGS } from "../constants/categories";
import { SEED_PODS } from "../services/data/seedPods";
import {
  getFastPodcastStations,
  hydrateStation,
  mapPodcastToStation,
  searchPodcastIndex,
} from "../services/podcastService";
import {
  searchStations,
  getTopStations,
  getStationsByLocation,
} from "../services/radioService";
import { getCountryFromCoordinates } from "../services/regionService";
import { Station, SupabasePodcastRow } from "../types";

export const useStationData = (
  _countries: { name: string; stationcount: number }[],
) => {
  const [stations, setStations] = useState<Station[]>([]);
  const [loadingStations, setLoadingStations] = useState(true);
  const [activeType, setActiveType] = useState<
    "radio" | "pods" | "curator" | "favs"
  >("radio");
  const [selectedCountry, setSelectedCountry] = useState("All");
  const [selectedTheme, setSelectedTheme] = useState("");
  const [stationSearchQuery, setStationSearchQuery] = useState("");
  const [locationError, setLocationError] = useState<string | null>(null);
  const [podcastCounts, setPodcastCounts] = useState<Record<string, number>>(
    {},
  );

  const [limit, setLimit] = useState(20);
  const [hasMore, setHasMore] = useState(true);

  // Load initial data
  useEffect(() => {
    const init = async () => {
      setLoadingStations(true);
      const tops = await getTopStations();
      setStations(tops);
      setLoadingStations(false);
    };
    init();

    // Calculate podcast counts from Supabase AND Seed Data
    import("../services/supabase").then(async ({ supabase }) => {
      const counts: Record<string, number> = {};

      // 1. Count from Seeds
      SEED_PODS.forEach((p) => {
        if (p.country) {
          counts[p.country] = (counts[p.country] || 0) + 1;
        }
      });

      // 2. Count from Supabase (if any)
      if (!supabase) {
        setPodcastCounts(counts);
        return;
      }

      const { data } = await supabase.from("podcasts").select("country");
      if (data) {
        data.forEach((p: SupabasePodcastRow) => {
          counts[p.country] = (counts[p.country] || 0) + 1;
        });
      }

      setPodcastCounts(counts);
    });
  }, []);

  const loadFilteredStations = async (
    type: string,
    theme: string,
    country: string,
  ) => {
    setLoadingStations(true);
    setStations([]);
    setLimit(20); // Reset limit
    setHasMore(true);

    if (type === "pods") {
      // 1. INSTANT LOAD: Get what we have (Cache or Seeds) immediately
      const initialStations = await getFastPodcastStations(theme, country);
      setStations(initialStations);
      setLoadingStations(false); // UI is now interactive!
      setHasMore(false); // Podcasts currently don't support pagination in this view

      // 2. BACKGROUND HYDRATION
      // Queue up hydration for all displayed stations.
      // We use a batched process to avoid spamming network too hard.

      const hydrationQueue = [...initialStations];

      const processQueue = async () => {
        const batch = hydrationQueue.splice(0, 4); // Process 4 at a time
        if (batch.length === 0) return;

        await Promise.all(
          batch.map(async (station) => {
            try {
              // Only hydrate if it needs loading
              if (!station.isLoadingData) return;

              const updated = await hydrateStation(station.id);

              setStations((prev) => {
                // IF FETCH FAILED (updated is null): REMOVE STATION
                if (!updated) {
                  // Filter out the station completely
                  return prev.filter((p) => p.id !== station.id);
                }
                // IF SUCCESS: UPDATE STATION in place
                return prev.map((p) => (p.id === updated.id ? updated : p));
              });
            } catch (_e) {
              // Safety catch: remove if severe error
              setStations((prev) => prev.filter((p) => p.id !== station.id));
            }
          }),
        );

        if (hydrationQueue.length > 0) {
          setTimeout(processQueue, 100);
        }
      };

      // Start the background process shortly after render
      setTimeout(processQueue, 100);
      return;
    }

    let searchTag = theme;
    if (type === "curator") {
      searchTag = theme || "curated";
    }

    const results = await searchStations({
      tag: searchTag,
      country: country,
      limit: 20,
    });

    setStations(results);
    setHasMore(results.length === 20);
    setLoadingStations(false);
  };

  const loadMore = async () => {
    if (!hasMore || loadingStations) return;

    const newLimit = limit + 20;
    setLimit(newLimit);

    // Don't set loadingStations to true to avoid full screen spinner,
    // maybe we need a separate loadingMore state, but for now let's just fetch.

    let searchTag = selectedTheme;
    if (activeType === "curator") {
      searchTag = selectedTheme || "curated";
    }

    const results = await searchStations({
      tag: searchTag,
      country: selectedCountry,
      limit: newLimit,
    });

    setStations(results);
    setHasMore(results.length === newLimit);
  };

  const handleNearMe = (
    setWeatherContext?: (weather: string) => void,
    setLocationContext?: (location: string) => void,
  ) => {
    if (!navigator.geolocation) {
      setLocationError("Din webbläsare stödjer inte platstjänster.");
      return;
    }
    setLoadingStations(true);
    setActiveType("radio");
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        // Set location context for AI DJ
        if (setLocationContext) {
          setLocationContext(`${latitude.toFixed(2)}, ${longitude.toFixed(2)}`);
        }

        // OPTIMIZATION: Run all operations in parallel
        const [localStations, detectedCountry, weather] = await Promise.all([
          // 1. Fetch nearby stations immediately
          getStationsByLocation(latitude, longitude),

          // 2. Detect country with timeout fallback
          (async () => {
            try {
              return await Promise.race([
                getCountryFromCoordinates(latitude, longitude),
                new Promise<null>((resolve) =>
                  setTimeout(() => resolve(null), 3000),
                ),
              ]);
            } catch (e) {
              console.warn("Country detection failed:", e);
              return null;
            }
          })(),

          // 3. Fetch weather if callback provided
          (async () => {
            if (setWeatherContext) {
              const { getLocalWeather } =
                await import("../services/weatherService");
              return await getLocalWeather(latitude, longitude);
            }
            return "";
          })(),
        ]);

        // Update weather context
        if (setWeatherContext && weather) {
          setWeatherContext(weather);
        }

        console.log("Near Me: Detected Country via API:", detectedCountry);

        // PROGRESSIVE UI UPDATE: Show nearby stations immediately
        setStations(localStations);
        setLoadingStations(false);

        // Then apply country filter in background
        if (detectedCountry) {
          setSelectedCountry(detectedCountry);
          // Load the full country view (Top, Genres, etc.)
          await loadFilteredStations("radio", "", detectedCountry);
        } else {
          // Fallback: Keep showing nearby stations
          console.warn(
            "Near Me: Geocoding failed or timed out, showing nearby stations",
          );
          setSelectedCountry("All");
        }
      },
      (err) => {
        console.error("Geo Error:", err);
        let msg = "Kunde inte komma åt din plats.";
        if (err.code === 1)
          msg =
            "Du nekade åtkomst till din plats. Välj land manuellt istället.";
        else if (err.code === 2)
          msg =
            "Din plats kunde inte hittas. Kontrollera dina platsinställningar.";
        else if (err.code === 3)
          msg =
            "Förfrågan tog för lång tid. Försök igen eller välj land manuellt.";

        setLocationError(msg);
        setLoadingStations(false);
        setSelectedCountry("All");
      },
      {
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 5 * 60 * 1000, // Cache position for 5 minutes
      },
    );
  };

  const handleManualSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const rawTerm = stationSearchQuery.trim();
    if (!rawTerm) return;

    const term = rawTerm.toLowerCase();

    // 1. Check for Genre/Theme Match (Direct Navigation)
    const mappedTheme = THEME_MAPPINGS[term];
    if (mappedTheme) {
      setSelectedTheme(mappedTheme);
      setStationSearchQuery("");
      let targetType = activeType;
      if (targetType === "favs" || targetType === "curator")
        targetType = "radio";
      if (targetType !== activeType) setActiveType(targetType);
      await loadFilteredStations(targetType, mappedTheme, selectedCountry);
      return;
    }

    // 3. GLOBAL SEARCH (Radio + Podcasts)
    setLoadingStations(true);
    setSelectedTheme("");
    setLimit(20);
    setHasMore(false); // Search results are usually limited by the API call anyway

    try {
      // Run searches in parallel
      const [radioResults, podcastRawResults] = await Promise.all([
        searchStations({
          name: rawTerm,
          country: selectedCountry !== "All" ? selectedCountry : undefined,
          limit: 20,
        }),
        searchPodcastIndex(rawTerm),
      ]);

      // Map podcasts to Station format
      const podcastResults = podcastRawResults.map((p) =>
        mapPodcastToStation(p),
      );

      // Combine results (Radio first, then Podcasts)
      // We can let the UI decide how to group them, but here we just provide the flat list
      setStations([...radioResults, ...podcastResults]);
    } catch (e) {
      console.error("Global search failed", e);
      setStations([]);
    }

    setLoadingStations(false);
  };

  return {
    stations,
    setStations,
    loadingStations,
    setLoadingStations,
    activeType,
    setActiveType,
    selectedCountry,
    setSelectedCountry,
    selectedTheme,
    setSelectedTheme,
    stationSearchQuery,
    setStationSearchQuery,
    locationError,
    loadFilteredStations,
    handleNearMe,
    handleManualSearch,
    podcastCounts,
    loadMore,
    hasMore,
  };
};
